import { ReactElement } from 'react'
import { useForm } from '@tanstack/react-form'
import * as z from 'zod'
import axios from 'src/utilities/http-client'
import {
  getZodValidationError,
  shouldShowZodFieldError,
  zodValidationSchema,
} from 'src/v3/utilities/validation'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import StateDropdown from 'src/v3/components/form-fields/state-dropdown'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import { useAppDispatch } from 'src/store/hooks'
import { LinkedService } from 'src/v3/core/types'
import { fetchLinkedServices } from '../../admin.slice'

interface LinkedServiceAddressFormFields {
  address: string
  city: string
  state: string
  zip: string
}

interface LinkedServiceAddressFormProps {
  isOpen: boolean
  linkedService?: LinkedService
  onClose: () => void
  isSaving: (bool: boolean) => void
  onError?: (error: string) => void
}

export let LinkedServiceAddressForm = ({
  isOpen,
  linkedService,
  onClose,
  isSaving,
  onError,
}: LinkedServiceAddressFormProps): ReactElement => {
  let dispatch = useAppDispatch()

  let formValues: LinkedServiceAddressFormFields = {
    address: linkedService?.address || 'PLACEHOLDER',
    city: linkedService?.city || 'PLACEHOLDER',
    state:
      linkedService?.state && linkedService?.state !== 'Other'
        ? linkedService?.state
        : 'Delaware',
    zip: linkedService?.zip || '55555',
  }

  let validationSchema = z.object({
    address: z.string().nonempty('Address is Required'),
    city: z.string().nonempty('City is Required'),
    state: z
      .string()
      .nonempty('State is Required')
      .refine((val) => val !== 'unselected', {
        message: 'State is Required',
      }),
    zip: zodValidationSchema.postalCode.nonempty('Zip Code is Required'),
  })

  let form = useForm({
    defaultValues: formValues,
    validators: {
      onChange: validationSchema,
    },
    onSubmit: async ({ value }) => {
      onClose()
      isSaving(true)
      axios
        .put('/api/v2/admin/linked-service/address', {
          id: linkedService?.id,
          address: value.address,
          city: value.city,
          state: value.state,
          zip: value.zip,
        })
        .then(() => dispatch(fetchLinkedServices()))
        .then(() => form.reset())
        .then(() => isSaving(false))
        .catch((err) => {
          if (onError) {
            onError(
              (err?.response?.data || err?.message) ??
                'Error Updating Linked Service Address',
            )
          }

          form.reset()
          isSaving(false)
          dispatch(fetchLinkedServices())
        })
    },
  })

  let handleOnClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={() => onClose()}>
      <Paper sx={{ minWidth: 300, p: 3 }}>
        <DialogTitle sx={{ textAlign: 'center', p: 0, mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Edit Address
          </Typography>
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent sx={{ p: 0, mb: 3 }}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}
            >
              <form.Field
                name="address"
                children={(field) => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={field.state.value}
                    label="Address"
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    helperText={
                      shouldShowZodFieldError(field.state.meta) &&
                      getZodValidationError(field.state.meta.errors)
                    }
                  />
                )}
              />
              <form.Field
                name="city"
                children={(field) => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={field.state.value}
                    label="City"
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    helperText={
                      shouldShowZodFieldError(field.state.meta) &&
                      getZodValidationError(field.state.meta.errors)
                    }
                  />
                )}
              />
              <form.Field
                name="state"
                children={(field) => (
                  <StateDropdown
                    variant="outlined"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    bgDefault
                  />
                )}
              />
              <form.Field
                name="zip"
                children={(field) => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={field.state.value}
                    label="Zip Code"
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    helperText={
                      shouldShowZodFieldError(field.state.meta) &&
                      getZodValidationError(field.state.meta.errors)
                    }
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: 3,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isSubmitting || !state.isValid}
              children={(isDisabled) => (
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={isDisabled}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              onClick={handleOnClose}
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}
